/**
 * Aba "Barcos" da barra lateral.
 *
 * Curto, como os outros painéis: liga a camada, explica as formas e as cores,
 * filtra por família e mostra o estado dos dados. Aparência fica atrás da
 * engrenagem.
 *
 * Duas coisas são FIXAS aqui e não podem ser recolhidas: o aviso de que a
 * recepção é ilhada, e o de que isto não serve para navegação.
 */
import { CircleQuestionMark, ExternalLink, History, RadioTower, RefreshCw, Settings2, Ship } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatInt } from '../lib/format';
import { formatDayTimeBrt, formatRelative } from '../lib/time';
import { useTimeStore } from '../state/time';
import { posicaoEm } from './track';
import { Badge, Button, Dialog, Hint, IconButton, Section, Slider, Toggle } from '../ui/primitives';
import { avisoDeFrescor } from './frescor';
import { FAMILIAS, NOME_FAMILIA, tipoDe, type Familia } from './kinds';
import { COR_FAMILIA } from './look';
import { LIMITES, familiaVisivel, useShipStore } from './store';
import { carregarNavios } from './sync';

/** Seta e losango em miniatura, nas mesmas formas do mapa. */
function Simbolo(props: { forma: 'seta' | 'losango'; cor?: string; vazado?: boolean; size?: number }) {
  const s = props.size ?? 13;
  const d = props.forma === 'seta' ? 'M0,-1 L0.62,0.85 L0,0.38 L-0.62,0.85 Z' : 'M0,-0.8 L0.8,0 L0,0.8 L-0.8,0 Z';
  return (
    <svg viewBox="-1.2 -1.2 2.4 2.4" width={s} height={s} aria-hidden className="shrink-0">
      <path
        d={d}
        fill={props.vazado ? 'none' : (props.cor ?? '#b8b8b8')}
        stroke={props.vazado ? (props.cor ?? '#b8b8b8') : 'rgba(10,16,28,0.85)'}
        strokeWidth={props.vazado ? 0.22 : 0.16}
        strokeDasharray={props.vazado ? '0.3 0.25' : undefined}
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Legenda() {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-300">
        <span className="inline-flex items-center gap-1">
          <Simbolo forma="seta" cor={COR_FAMILIA.carga} /> navegando (a seta é o rumo)
        </span>
        <span className="inline-flex items-center gap-1">
          <Simbolo forma="losango" cor={COR_FAMILIA.carga} /> parada ou atracada
        </span>
        <span className="inline-flex items-center gap-1">
          <Simbolo forma="losango" cor={COR_FAMILIA.carga} vazado /> última posição conhecida
        </span>
        <span className="inline-flex items-center gap-1">
          <svg viewBox="-1.2 -1.2 2.4 2.4" width={13} height={13} aria-hidden className="shrink-0">
            <circle cx="0" cy="0" r="0.5" fill="none" stroke="rgba(200,210,225,0.75)" strokeWidth="0.17" />
          </svg>
          embarcação pequena, sem identificação
        </span>
      </div>
    </div>
  );
}

function FiltroFamilias({ contagem }: { contagem: Map<Familia, number> }) {
  const estilo = useShipStore((s) => s.estilo);
  const alternar = useShipStore((s) => s.alternarFamilia);

  return (
    <div className="flex flex-wrap gap-1 pt-2">
      {FAMILIAS.filter((f) => (contagem.get(f) ?? 0) > 0).map((f) => {
        const on = familiaVisivel(estilo, f);
        return (
          <button
            key={f}
            type="button"
            onClick={() => alternar(f)}
            aria-pressed={on}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] transition pointer-coarse:py-2 ${
              on ? 'border-white/20 bg-white/10 text-ink-100' : 'border-white/10 text-ink-400 hover:text-ink-200'
            }`}
          >
            <span
              className="inline-block size-2 rounded-[2px]"
              style={{ background: COR_FAMILIA[f], opacity: on ? 1 : 0.35 }}
              aria-hidden
            />
            {NOME_FAMILIA[f]}
            <span className="tabular-nums text-ink-400">{contagem.get(f)}</span>
          </button>
        );
      })}
    </div>
  );
}

function AparenciaDialog(props: { open: boolean; onClose: () => void }) {
  const estilo = useShipStore((s) => s.estilo);
  const setEstilo = useShipStore((s) => s.setEstilo);
  return (
    <Dialog title="Aparência das embarcações" open={props.open} onClose={props.onClose}>
      <div className="space-y-4 p-4">
        <Slider label="Tamanho" value={estilo.tamanho} {...LIMITES.tamanho} onChange={(tamanho) => setEstilo({ tamanho })} />
        <Slider
          label="Opacidade"
          value={estilo.opacidade}
          {...LIMITES.opacidade}
          onChange={(opacidade) => setEstilo({ opacidade })}
        />
        <Toggle
          label="Mostrar a área com recepção de AIS"
          checked={estilo.mostrarEscuta}
          onChange={(mostrarEscuta) => setEstilo({ mostrarEscuta })}
          description="Mancha clara onde houve recepção nos últimos 7 dias. Fora dela, o mapa não sabe se há navegação."
        />
        <Toggle
          label="Mostrar embarcações fora de alcance"
          checked={estilo.mostrarSumidas}
          onChange={(mostrarSumidas) => setEstilo({ mostrarSumidas })}
          description="Símbolo vazado e tracejado na última posição conhecida, que pode ter horas."
        />
      </div>
    </Dialog>
  );
}

export function ShipPanel() {
  const visivel = useShipStore((s) => s.visivel);
  const situacao = useShipStore((s) => s.situacao);
  const dados = useShipStore((s) => s.dados);
  const setVisivel = useShipStore((s) => s.setVisivel);
  const [aparenciaAberta, setAparenciaAberta] = useState(false);

  const aviso = avisoDeFrescor(dados.gerado);

  const contagem = useMemo(() => {
    const m = new Map<Familia, number>();
    for (const n of dados.navios) {
      const f = tipoDe(n.ficha?.tipo ?? null).familia;
      m.set(f, (m.get(f) ?? 0) + 1);
    }
    return m;
  }, [dados.navios]);

  const navegando = useMemo(() => dados.navios.filter((n) => (n.sog ?? 0) >= 0.5).length, [dados.navios]);
  const creditos = Object.values(dados.atribuicao);

  /**
   * Quantas embarcações têm posição no QUADRO exibido.
   *
   * A trilha só existe a partir do momento em que o pipeline começou a
   * gravá-la. Quem arrasta o loop para antes disso vê a frota sumir — o que é
   * a verdade (não sabemos onde elas estavam), mas parece defeito se ninguém
   * avisar. Esta contagem é o aviso.
   */
  const displayedTime = useTimeStore((s) => s.displayedTime);
  const currentTime = useTimeStore((s) => s.currentTime);
  const instante = displayedTime ?? currentTime;

  const noQuadro = useMemo(
    () => dados.navios.filter((n) => posicaoEm(n.trilha, instante) !== null).length,
    [dados.navios, instante],
  );

  /** Amostra mais antiga que existe: quando a gravação da trilha começou. */
  const inicioHistorico = useMemo(() => {
    let min = Infinity;
    for (const n of dados.navios) {
      const p = n.trilha[0];
      if (p && p[0] < min) min = p[0];
    }
    return Number.isFinite(min) ? min : null;
  }, [dados.navios]);

  const faltando = dados.navios.length - noQuadro;

  return (
    <div>
      <Section
        title="Embarcações"
        icon={Ship}
        right={
          visivel && (
            <IconButton
              icon={Settings2}
              size="sm"
              label="Aparência das embarcações"
              onClick={() => setAparenciaAberta(true)}
              className="pointer-coarse:size-11"
            />
          )
        }
      >
        <Toggle
          label="Navios, balsas e barcos"
          checked={visivel}
          onChange={setVisivel}
          description="Posições de AIS na Amazônia, sincronizadas com o quadro exibido."
        />

        {visivel && aviso && (
          <div className="mt-2 rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            {aviso}
          </div>
        )}

        {visivel && (
          <>
            {dados.navios.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 pt-2.5">
                <Badge tone="rio">{formatInt(dados.navios.length)} embarcações</Badge>
                {navegando > 0 && <Badge tone="mata">{formatInt(navegando)} navegando</Badge>}
                {dados.anonimos.length > 0 && <Badge>{formatInt(dados.anonimos.length)} sem identificação</Badge>}
              </div>
            )}

            {/*
              O quadro exibido é anterior ao que a trilha alcança: a frota
              "some" ao arrastar o loop para trás, e sem este aviso isso
              parece defeito em vez de ausência de dado.
            */}
            {faltando > 0 && dados.navios.length > 0 && (
              <div className="mt-2.5 flex items-start gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-[11px] leading-snug text-ink-300">
                <History size={14} className="mt-px shrink-0 text-ink-400" aria-hidden />
                <span>
                  <strong className="text-ink-200">{formatInt(noQuadro)} de {formatInt(dados.navios.length)}</strong> têm
                  posição no quadro exibido. O percurso só existe a partir de{' '}
                  {inicioHistorico ? formatDayTimeBrt(inicioHistorico) : 'quando a gravação começou'} — antes disso o
                  portal não sabe onde a embarcação estava, e prefere não desenhar.
                </span>
              </div>
            )}

            {/* FIXO: a leitura errada mais provável desta camada inteira. */}
            <div className="mt-2.5 flex items-start gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-[11px] leading-snug text-ink-300">
              <RadioTower size={14} className="mt-px shrink-0 text-ink-400" aria-hidden />
              <span>
                O AIS só é ouvido perto dos receptores — hoje, Belém, Manaus e o Tapajós. A mancha clara mostra onde há
                recepção. <strong className="text-ink-200">Rio sem barco fora dela quase sempre é falta de escuta, não
                rio vazio.</strong>
              </span>
            </div>

            <FiltroFamilias contagem={contagem} />

            <div className="pt-3">
              <Legenda />
            </div>

            <Hint>
              A seta aponta para onde a embarcação vai. Clique numa para ver a ficha e o percurso das últimas 24 h.
              Posições como recebidas: <strong>não use para navegação.</strong>
            </Hint>

            {/*
              Crédito visível enquanto a camada estiver ligada, e não só dentro
              da seção recolhida "O que é". A licença do AIS é por fonte: cada
              uma aparece com o seu próprio texto, como veio, sem concatenar.
            */}
            {creditos.length > 0 && (
              <div className="mt-2 space-y-0.5 border-t border-white/10 pt-2 text-[10px] leading-snug text-ink-400">
                {creditos.map((c) => (
                  <p key={c}>{c}</p>
                ))}
              </div>
            )}
          </>
        )}
      </Section>

      <Section title="Dados" icon={RefreshCw} defaultOpen={false}>
        <div className="space-y-1.5 text-xs text-ink-300">
          {situacao.estado === 'loading' && <div>Baixando…</div>}
          {situacao.estado === 'error' && <div className="text-alerta-300">Falha ao baixar: {situacao.erro}</div>}
          {situacao.mensagem && <div className="text-alerta-300">{situacao.mensagem}</div>}
          {situacao.estado === 'ready' && (
            <div>
              {formatInt(dados.navios.length)} embarcações
              {dados.gerado !== null && <> · atualizado {formatRelative(dados.gerado)}</>}
            </div>
          )}
          <Button icon={RefreshCw} onClick={() => void carregarNavios(true)}>
            Atualizar agora
          </Button>
        </div>
      </Section>

      <Section title="O que é" icon={CircleQuestionMark} defaultOpen={false}>
        <div className="space-y-2 text-xs leading-relaxed text-ink-300">
          <p>
            Toda embarcação comercial é obrigada a transmitir a própria posição pelo <strong>AIS</strong>, um rádio que
            anuncia quem ela é, para onde vai e a que velocidade. Receptores em terra escutam essas transmissões, e o
            AmaView mostra no mapa o que foi ouvido na Amazônia.
          </p>
          <p>
            A cor diz a família (carga, tanque, passageiros…) e a forma diz se está andando ou parada. Barcos pequenos
            de transponder voluntário aparecem como ponto, <strong>sem nome e sem MMSI</strong> — e os de recreio e
            vela não são publicados.
          </p>
          <p>
            O destino e a hora de chegada são <strong>declarados pela própria embarcação</strong>, digitados à mão a
            bordo. Valem como declaração, não como fato.
          </p>

          {/*
            Crédito por fonte, como texto puro. O aiscast é explícito: licenças
            não se fundem, então cada fonte aparece com o seu próprio texto,
            exatamente como veio.
          */}
          {creditos.length > 0 && (
            <div className="space-y-0.5 pt-1 text-ink-400">
              {creditos.map((c) => (
                <p key={c}>{c}</p>
              ))}
            </div>
          )}
          <p className="text-ink-400">
            <a
              href="https://openwaters.io/ais/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-rio-300 hover:underline"
            >
              openwaters.io/ais
              <ExternalLink size={10} />
            </a>
          </p>
        </div>
      </Section>

      <AparenciaDialog open={aparenciaAberta} onClose={() => setAparenciaAberta(false)} />
    </div>
  );
}
